import Vue from "vue";

export const imagePreviewBus = new Vue();

const PLACEHOLDER_HINTS = [
  "user-profile.png",
  "uploadfile.png",
  "roomround-logo",
  "placeholder",
];

export function isPreviewableImageSrc(src) {
  if (!src || typeof src !== "string") return false;
  const value = src.trim();
  if (!value || value === "null" || value === "undefined") return false;
  if (value.startsWith("data:image")) return true;
  if (/\.svg(\?|#|$)/i.test(value)) return false;
  if (value.startsWith("/img/")) return false;
  if (PLACEHOLDER_HINTS.some((hint) => value.toLowerCase().includes(hint))) {
    return false;
  }
  // Absolute hosted / CDN / API media URLs
  if (/^https?:\/\//i.test(value)) return true;
  // Relative uploaded keys sometimes rendered without base — skip bare paths
  return false;
}

export function resolveImageSrcFromEventTarget(target) {
  if (!target || !target.closest) return null;

  const explicit = target.closest("[data-image-preview]");
  if (explicit) {
    const bound =
      explicit.getAttribute("data-image-preview") ||
      explicit.getAttribute("data-preview-src");
    if (bound && bound !== "true" && isPreviewableImageSrc(bound)) return bound;
  }

  const img = target.closest("img");
  if (img && img.getAttribute("src")) {
    const src = img.currentSrc || img.src || img.getAttribute("src");
    if (isPreviewableImageSrc(src)) return src;
  }

  // Vuetify v-img renders background-image on .v-image__image
  const vImageEl =
    target.closest(".v-image__image") ||
    target.closest(".v-image") ||
    (target.classList && target.classList.contains("v-image__image")
      ? target
      : null);
  if (vImageEl) {
    const style = window.getComputedStyle(vImageEl);
    const bg = style && style.backgroundImage;
    if (bg && bg !== "none") {
      const match = bg.match(/url\(["']?(.*?)["']?\)/i);
      if (match && match[1] && isPreviewableImageSrc(match[1])) {
        return match[1];
      }
    }
    const nestedImg = vImageEl.querySelector && vImageEl.querySelector("img");
    if (nestedImg) {
      const src =
        nestedImg.currentSrc || nestedImg.src || nestedImg.getAttribute("src");
      if (isPreviewableImageSrc(src)) return src;
    }
  }

  return null;
}

function openPreview(url) {
  if (!isPreviewableImageSrc(url)) return;
  imagePreviewBus.$emit("open", url);
}

function onDocumentClick(event) {
  // Ignore non-left clicks / modified clicks meant for new tab etc.
  if (event.defaultPrevented) return;
  if (event.button != null && event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  // Skip when a local preview dialog already owns this image
  if (event.target.closest && event.target.closest("[data-no-image-preview]")) {
    return;
  }

  const src = resolveImageSrcFromEventTarget(event.target);
  if (!src) return;

  event.preventDefault();
  event.stopPropagation();
  openPreview(src);
}

function markPreviewCursor(event) {
  if (event.target.closest && event.target.closest("[data-no-image-preview]")) {
    return;
  }
  const src = resolveImageSrcFromEventTarget(event.target);
  if (!src) return;
  const el =
    event.target.closest("img") ||
    event.target.closest(".v-image") ||
    event.target.closest("[data-image-preview]");
  if (el && el.style) {
    el.style.cursor = "zoom-in";
  }
}

export default {
  install(VueCtor) {
    VueCtor.prototype.$previewImage = openPreview;

    VueCtor.directive("image-preview", {
      bind(el, binding) {
        el.setAttribute("data-image-preview", "true");
        el.classList.add("image-previewable");
        el.style.cursor = "zoom-in";

        const handler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const bound = binding.value;
          let url = null;
          if (typeof bound === "string" && bound) {
            url = bound;
          } else if (bound && typeof bound === "object" && bound.src) {
            url = bound.src;
          } else {
            url =
              resolveImageSrcFromEventTarget(el) ||
              el.getAttribute("src") ||
              (el.querySelector &&
                el.querySelector("img") &&
                el.querySelector("img").src);
          }
          if (url) openPreview(url);
        };

        el.__imagePreviewHandler = handler;
        el.addEventListener("click", handler);
      },
      update(el, binding) {
        if (typeof binding.value === "string" && binding.value) {
          el.setAttribute("data-preview-src", binding.value);
        }
      },
      unbind(el) {
        if (el.__imagePreviewHandler) {
          el.removeEventListener("click", el.__imagePreviewHandler);
          delete el.__imagePreviewHandler;
        }
      },
    });

    if (typeof document !== "undefined") {
      document.addEventListener("click", onDocumentClick, true);
      document.addEventListener("mouseover", markPreviewCursor, true);
    }
  },
};
